import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/Button'
import { Icone } from '@/components/ui/Icone'
import * as auth from '@/lib/api/auth'
import type { Me } from '@/lib/api/auth'

/**
 * Le rappel de vérification — RG-1.3.
 *
 * Ni bloquant, ni silencieux. Bloquer à l'inscription ferait perdre ceux qui
 * n'ont pas leur boîte à portée de main ; ne rien dire les laisserait
 * découvrir la fermeture au huitième jour, sans prévenir.
 *
 * Le ton se durcit à mesure que l'échéance approche, et le chiffre est
 * TOUJOURS présent : « il te reste 2 jours » se comprend, « pense à vérifier
 * ton adresse » ne dit rien de l'urgence.
 */
export function VerificationBanner({ me }: { me: Me }) {
  const client = useQueryClient()

  const renvoyer = useMutation({
    mutationFn: auth.resendVerification,
  })

  if (me.email_verified) return null

  const jours = me.verification_grace_days
  const urgent = jours <= 2

  return (
    <section
      // `alert` seulement à l'approche : annoncer sept jours à l'avance sur
      // le même ton entraîne à ignorer les alertes suivantes.
      role={urgent ? 'alert' : 'status'}
      className="flex flex-col gap-2 border-b p-4"
      style={{
        borderColor: 'var(--bordure)',
        background: 'var(--surface-2)',
      }}
    >
      <h2 className="text-[15px] font-bold">
        {jours === 0
          ? 'Confirme ton adresse e-mail pour continuer'
          : `Confirme ton adresse e-mail — il te reste ${String(jours)} ${jours === 1 ? 'jour' : 'jours'}`}
      </h2>

      <p className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
        {jours === 0
          ? 'Ton fil et tes thèmes sont fermés jusqu’à confirmation. Ton compte et ta progression sont intacts.'
          : 'Tu peux réviser sans attendre. Passé ce délai, l’accès au contenu se ferme jusqu’à confirmation.'}
      </p>

      <div className="flex items-center gap-3">
        <Button
          size="dense"
          variant={urgent ? 'primary' : 'outline'}
          loading={renvoyer.isPending}
          onClick={() => {
            renvoyer.mutate(undefined, {
              onSuccess: () => {
                void client.invalidateQueries({ queryKey: ['me'] })
              },
            })
          }}
        >
          Renvoyer le lien
        </Button>

        {renvoyer.isSuccess && (
          <span className="text-[13px]" style={{ color: 'var(--juste)' }}>
            <Icone nom="coche" taille={14} /> Envoyé — regarde aussi tes indésirables.
          </span>
        )}
        {renvoyer.isError && (
          <span className="text-[13px]" style={{ color: 'var(--faux)' }}>
            <Icone nom="croix" taille={14} /> L’envoi a échoué. Réessaie dans une minute.
          </span>
        )}
      </div>
    </section>
  )
}
